import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreditCardBrand, Nullable } from '../../../../types';
import { catchFatalError } from '../../../../utils/utils';

import {
  AllErrors,
  AdyenState,
  areIdenticalCardErrors,
  DEFAULT_ADYEN_STATE,
  DEFAULT_ERRORS,
  getFromAdyenCardBrand,
  getLastFourDigits,
  maybeErrorForHolderName,
  parseStateOnChange,
  isAdyenStateValid,
  addRequiredMessageIfNeeded,
  AdyenCreditCardFormData,
  convertAdyenState,
  getFocusedElement,
} from './useAdyenState.static';

/**
 * Because we use the 'securedfields' of AdyenCheckout, we need to pass several callbacks to AdyenCheckout in order to gather the encrypted information.
 *
 * This hook is providing these necessary callbacks and collect everything into a ref.
 *
 * In addition, it also collects the holderName and the storeInVault data, as the associated inputs are not covered by the 'securedfields' approach.
 *
 * For validation and retrieval of the encrypted data, this hook provides the function 'computeAdyenCreditCardFormData'.
 */
const useAdyenState = (onFatalError: (error: unknown) => void) => {
  const adyenStateRef = useRef<AdyenState>({
    ...DEFAULT_ADYEN_STATE, // deep copy: the reference to the DEFAULT_ADYEN_STATE constant should not be used since we are modifying the object in this hook
    paymentMethod: { ...DEFAULT_ADYEN_STATE.paymentMethod },
  });
  const [storeInVault, setStoreInVault] = useState(DEFAULT_ADYEN_STATE.storeInVault);
  const [creditCardBrand, setCreditCardBrand] = useState<CreditCardBrand | null>(
    DEFAULT_ADYEN_STATE.creditCardBrand,
  );
  const [focusedElement, setFocusedElement] = useState<string | null>(null);
  const [errors, setErrors] = useState<AllErrors>({ ...DEFAULT_ERRORS });
  const { t } = useTranslation();

  const requiredMessage = t('checkout.form.required.error');

  // The holderName is not managed by Adyen so we keep track of the changes ourselves.
  const onHolderNameChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    catchFatalError(onFatalError, (event: ChangeEvent<HTMLInputElement>) => {
      const holderName = event.target.value;
      adyenStateRef.current.paymentMethod.holderName = holderName;

      setErrors((errors) => {
        const errorForHolderName = maybeErrorForHolderName(holderName, t);
        // if no change in errors, return the given 'errors' to tell React not to re-render
        return errors.holderName === errorForHolderName
          ? errors
          : { ...errors, holderName: errorForHolderName };
      });
    }),
    [adyenStateRef, onFatalError],
  );

  // We use the Adyen 'onFieldValid' callback only to get the last four digits of the card number.
  // The actual validity of fields is managed with 'onChange' below.
  const onFieldValid = useCallback(
    catchFatalError(onFatalError, (data: unknown) => {
      const nullableLastFour = getLastFourDigits(data);
      // as 'onFieldValid' is called for every input but only the 'encryptedCardNumber' has the last four digits, we update the state only when needed
      if (nullableLastFour !== null) {
        adyenStateRef.current.lastFour = nullableLastFour;
      }
    }),
    [adyenStateRef, onFatalError],
  );

  // 'storeInVault' is also tracked with a hook to re-render the checkbox when storeInVault has changed
  const onStoreInVaultChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    catchFatalError(onFatalError, (event: ChangeEvent<HTMLInputElement>) => {
      const storeInVault = event.target.checked;
      adyenStateRef.current.storeInVault = storeInVault;
      setStoreInVault(storeInVault);
    }),
    [adyenStateRef, onFatalError],
  );

  // Adyen has the specific callback 'onBrand' for when it detects the brand of the card.
  // And 'brand' is also tracked with a hook to re-render the card icons and adapt the UI to Amex cards.
  const onBrand = useCallback(
    catchFatalError(onFatalError, (brandData: unknown) => {
      const creditCardBrand = getFromAdyenCardBrand(brandData);
      adyenStateRef.current.creditCardBrand = creditCardBrand;
      setCreditCardBrand(creditCardBrand);
    }),
    [adyenStateRef, onFatalError],
  );

  // Adyen provides the 'onChange' callback that is triggered when the customer entered his card information.
  // It contains the bulk of the data we need, as well as the validation errors of the fields, if any.
  const onChange = useCallback(
    catchFatalError(onFatalError, (state: unknown) => {
      const parsedState = parseStateOnChange(state);

      adyenStateRef.current.isValid = parsedState.isValid;
      Object.assign(adyenStateRef.current.paymentMethod, parsedState.paymentMethod);

      setErrors((errors) => {
        // if no change in errors, return the given 'errors' to tell React not to re-render
        return areIdenticalCardErrors(errors, parsedState.errors)
          ? errors
          : Object.assign(parsedState.errors, {
              holderName: errors.holderName,
            });
      });
    }),
    [adyenStateRef, onFatalError],
  );

  const onFocus = useCallback(
    catchFatalError(onFatalError, (data: unknown) => {
      setFocusedElement(getFocusedElement(data));
    }),
    [adyenStateRef, onFatalError],
  );

  // Return the state if all is fine: no errors on any field & all the encrypted data is present
  const computeAdyenCreditCardFormData = useCallback((): Nullable<AdyenCreditCardFormData> => {
    try {
      const state = adyenStateRef.current;

      if (isAdyenStateValid(state)) {
        return convertAdyenState(state);
      } else {
        setErrors((errors) => {
          const newErrors = addRequiredMessageIfNeeded(errors, state, requiredMessage);
          // no need to return a new 'errors' object if there's nothing to change
          return Object.keys(newErrors).length === 0 ? errors : { ...errors, ...newErrors };
        });
        return null;
      }
    } catch (error) {
      onFatalError(error);
      return null;
    }
  }, [adyenStateRef, requiredMessage, onFatalError]);

  return {
    adyenStateRef,
    onChange,
    onFieldValid,
    onHolderNameChange,
    storeInVault,
    onStoreInVaultChange,
    focusedElement,
    onFocus,
    creditCardBrand,
    onBrand,
    computeAdyenCreditCardFormData,
    errors,
  };
};

export default useAdyenState;
